import { useState, type FormEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/platformAuthStore';
import { theme } from '../theme';

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 56px)',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    background: theme.bgPanel,
    borderRadius: theme.radiusLg,
    border: `1px solid ${theme.border}`,
    padding: '32px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: theme.textPrimary,
    marginBottom: '24px',
    textAlign: 'center' as const,
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    color: theme.textSecondary,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: theme.radiusMd,
    border: `1px solid ${theme.border}`,
    background: theme.bgInput,
    color: theme.textPrimary,
    fontSize: '14px',
    outline: 'none',
  },
  btn: {
    width: '100%',
    padding: '10px',
    borderRadius: theme.radiusMd,
    border: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    background: theme.accent,
    color: theme.accentText,
    marginTop: '8px',
  },
  error: {
    color: theme.error,
    fontSize: '13px',
    marginBottom: '12px',
    textAlign: 'center' as const,
  },
  footer: {
    textAlign: 'center' as const,
    marginTop: '16px',
    fontSize: '13px',
    color: theme.textMuted,
  },
  link: {
    color: theme.accent,
    textDecoration: 'none',
  },
  notice: {
    color: theme.warning,
    fontSize: '13px',
    marginBottom: '12px',
    textAlign: 'center' as const,
  },
};

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, token, clearError, notice } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) navigate('/dashboard', { replace: true });
  }, [token, navigate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    clearError();
    login(email, password);
  };

  return (
    <div style={styles.container}>
      <form style={styles.card} onSubmit={handleSubmit}>
        <h2 style={styles.title}>Log In</h2>

        {error && <div style={styles.error}>{error}</div>}
        {notice && !error && <div style={styles.notice}>{notice}</div>}

        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button style={styles.btn} type="submit" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Log In'}
        </button>

        <div style={styles.footer}>
          <Link to="/forgot-password" style={styles.link}>
            Forgot password?
          </Link>
        </div>

        <div style={styles.footer}>
          Don't have an account?{' '}
          <Link to="/signup" style={styles.link}>
            Sign up
          </Link>
        </div>
      </form>
    </div>
  );
}
